import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import type { MentionSuggestionItem, MentionListExtraProps } from '../extensions/mention';
import { UserAvatar } from './UserAvatar';
import { UserName } from './UserName';

export const DM_MENTION_THIRD_PARTY_PLACEHOLDER = msg`Direct messages are for two people. Create a channel to include others.`;

const MentionList = forwardRef<
  { onKeyDown: (props: SuggestionKeyDownProps) => boolean },
  SuggestionProps<MentionSuggestionItem> & MentionListExtraProps
>((props, ref) => {
  const { i18n } = useLingui();
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex(i => (i + props.items.length - 1) % props.items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex(i => (i + 1) % props.items.length);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command({ id: item.id, label: item.label } as unknown as MentionSuggestionItem);
    }
  };

  if (props.items.length === 0) {
    if (props.channelType === 'dm') {
      return (
        <div
          className="mention-picker mention-picker-dm-empty"
          data-channel-type="dm"
          data-mention-empty="dm-third-party"
        >
          <span className="mention-empty-hint">{i18n._(DM_MENTION_THIRD_PARTY_PLACEHOLDER)}</span>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="mention-picker" data-channel-type={props.channelType ?? 'channel'}>
      {props.items.map((item, idx) => (
        <button
          key={item.id}
          className={`mention-option ${idx === selectedIndex ? 'mention-option-active' : ''}`}
          onMouseDown={(e) => {
            e.preventDefault();
            selectItem(idx);
          }}
        >
          <UserAvatar userId={item.id} displayName={item.label} size={20} />
          <span className="mention-name"><UserName displayName={item.label} role={item.role} /></span>
        </button>
      ))}
    </div>
  );
});

MentionList.displayName = 'MentionList';
export default MentionList;
